import { isEqual } from 'lodash-es';
import { DateTime } from 'luxon';
import { xhrStatusUIState } from 'redux/reusable/xhrStatus';
import { getIsUserAdmin, getIsUserManager, getUserTimeFormat } from 'redux/selectors/user';
import type { IState } from 'redux/state';
import { createSelector } from 'reselect';
import { formatTime } from 'utils/date-helper';
import { getAccountFeatures } from './accounts';

const readXhrStatus = (state: IState) => state.notificationSettings.xhrStatus;
export const readSettings = (state: IState) => state.notificationSettings.settings;
export const readDraft = (state: IState) => state.notificationSettings.draft;

export const getXhrStatus = createSelector([readXhrStatus], xhrStatus =>
  xhrStatusUIState(xhrStatus)
);

interface TimeOfDayOption {
  label: string;
  value: string;
}

export interface NotificationSettingsEditorUi {
  hasChanges: boolean;
  hasInitialized: boolean;
  isDiscardButtonVisible: boolean;
  isLoading: boolean;
  isSaveButtonDisabled: boolean;
  timeOfDayOptions: Array<TimeOfDayOption>;
}

const getDraftHasChanges = createSelector([readSettings, readDraft], (settings, draft): boolean => {
  if (!settings || !draft) return false;

  return !isEqual(settings, draft);
});

export const getTimeOfDayOptions = createSelector(
  [getUserTimeFormat],
  (userTimeFormat): Array<TimeOfDayOption> => {
    const options: Array<TimeOfDayOption> = [];
    for (let h = 0; h < 24; ++h) {
      const dateTimes = [
        DateTime.fromObject({ hour: h }) as DateTime<true>,
        DateTime.fromObject({ hour: h, minute: 30 }) as DateTime<true>,
      ];
      options.push(
        ...dateTimes.map(dt => ({
          value: dt.toLocaleString(DateTime.TIME_24_SIMPLE),
          label: formatTime(dt.toISO(), userTimeFormat),
        }))
      );
    }
    return options;
  }
);

export const getNotificationSettingsEditor = createSelector(
  [
    getXhrStatus,
    readSettings,
    readDraft,
    getDraftHasChanges,
    getTimeOfDayOptions,
    getAccountFeatures,
    getIsUserAdmin,
    getIsUserManager,
  ],
  (
    { hasInitialized, isSaving, isLoading },
    settings,
    draft,
    hasChanges,
    timeOfDayOptions,
    features,
    isAdmin,
    isManager
  ) => {
    const ui: NotificationSettingsEditorUi = {
      hasChanges,
      hasInitialized,
      isDiscardButtonVisible: hasChanges && !isSaving,
      isLoading,
      isSaveButtonDisabled: !hasChanges || isSaving,
      timeOfDayOptions,
    };

    return {
      settings,
      draft,
      features,
      isAdmin,
      isManager,
      ui,
    };
  }
);
